import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { User, Save, Phone, MapPin, Calendar, Briefcase, Home, Shield, Award, TrendingUp, CheckCircle, Sparkles, FileText, Users, Clock, Star, Hash, Building2 } from 'lucide-react';
import ImageUploader from './ImageUploader';
import PhoneInput from '../ui/PhoneInput';
import EmailInput from '../ui/EmailInput';
import { DatePickerFr } from '../ui/DatePickerFr';
import CountrySelect from '../ui/CountrySelect';
import UnifiedActivitySelector from '../UnifiedActivitySelector';
import ChangePasswordForm from './ChangePasswordForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MemberProfileEdit({ member, onUpdate, getAuthHeader }) {
  const [formData, setFormData] = useState({
    photo_profil_url: member?.photo_profil_url || '',
    email: member?.email || '',
    telephone: member?.telephone || '',
    nom: member?.nom || '',
    prenom: member?.prenom || '',
    adresse: member?.adresse || '',
    ville: member?.ville || '',
    code_postal: member?.code_postal || '',
    pays: member?.pays || 'France',
    date_naissance: member?.date_naissance ? member.date_naissance.split('T')[0] : '',
    profession: member?.profession || '',
    type_logement: member?.type_logement || '',
    nombre_foyer: member?.nombre_foyer || '',
    statut_emploi: member?.statut_emploi || '',
    genre: member?.genre || '',
    activite_professionnelle: member?.activite_professionnelle || null
  });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('identity');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUploaded = (url) => {
    setFormData(prev => ({ ...prev, photo_profil_url: url }));
    toast.success('Photo de profil mise à jour !');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSend = Object.fromEntries(
        Object.entries(formData).filter(([_, v]) => v !== '' && v !== null)
      );

      const response = await axios.put(
        `${BACKEND_URL}/api/users/me/profile`,
        dataToSend,
        { headers: getAuthHeader() }
      );

      toast.success('Profil mis à jour avec succès !');
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Calcul du pourcentage de complétion du profil
  const calculateCompletion = () => {
    const fields = ['nom', 'prenom', 'email', 'telephone', 'adresse', 'date_naissance', 'profession'];
    const filled = fields.filter(f => formData[f] && formData[f].toString().trim() !== '').length;
    return Math.round((filled / fields.length) * 100);
  };

  const completion = calculateCompletion();

  return (
    <div className="space-y-6">
      {/* En-tête avec carte adhérent et statistiques */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Carte Membre */}
        <Card className="lg:col-span-2 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white border-0 shadow-2xl overflow-hidden relative">
          <div className="absolute top-0 right-0 w-72 h-72 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          <CardContent className="pt-6 relative z-10">
            <div className="flex items-start gap-6">
              {/* Avatar */}
              <div className="relative">
                <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden border-2 border-white/30">
                  {formData.photo_profil_url ? (
                    <img src={formData.photo_profil_url} alt="Profil" className="w-full h-full object-cover" />
                  ) : (
                    <User className="h-10 w-10 text-white/80" />
                  )}
                </div>
                {completion === 100 && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-400 rounded-full flex items-center justify-center border-2 border-white">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="h-4 w-4 text-blue-200" />
                  <span className="text-sm font-medium text-blue-100">Membre Adhérent</span>
                </div>
                <h2 className="text-2xl font-bold mb-1">
                  {formData.prenom || 'Prénom'} {formData.nom || 'Nom'}
                </h2>
                {member?.numero_adherent && (
                  <p className="text-blue-200 font-mono text-lg tracking-wider">
                    N° {member.numero_adherent}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  {member?.statut === 'actif' && (
                    <Badge className="bg-slate-800 text-white border-slate-600 hover:bg-slate-700 font-semibold">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Compte actif
                    </Badge>
                  )}
                  {member?.date_adhesion && (
                    <Badge className="bg-slate-800 text-white border-slate-600 font-semibold">
                      <Clock className="h-3 w-3 mr-1" />
                      Depuis {new Date(member.date_adhesion).getFullYear()}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Carte Complétion */}
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0 shadow-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-slate-400">Profil complété</p>
                <p className="text-3xl font-bold">{completion}%</p>
              </div>
              <div className={`p-3 rounded-xl ${completion === 100 ? 'bg-emerald-500/20' : 'bg-blue-500/20'}`}>
                {completion === 100 ? (
                  <Award className="h-8 w-8 text-emerald-400" />
                ) : (
                  <TrendingUp className="h-8 w-8 text-blue-400" />
                )}
              </div>
            </div>
            <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-700 ${
                  completion === 100 
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-400' 
                    : 'bg-gradient-to-r from-blue-500 to-purple-500'
                }`}
                style={{ width: `${completion}%` }}
              />
            </div>
            {completion < 100 && (
              <p className="text-xs text-slate-400 mt-3 flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-yellow-400" />
                Complétez pour débloquer tous les avantages
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Navigation par sections */}
      <div className="flex flex-wrap gap-2 p-1 bg-slate-100 rounded-xl">
        {[
          { id: 'identity', label: 'Identité', icon: User },
          { id: 'contact', label: 'Coordonnées', icon: Phone },
          { id: 'professional', label: 'Professionnel', icon: Briefcase },
          { id: 'security', label: 'Sécurité', icon: Shield }
        ].map(section => (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeSection === section.id
                ? 'bg-white text-blue-600 shadow-md'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
          >
            <section.icon className="h-4 w-4" />
            <span className="hidden sm:inline">{section.label}</span>
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section: Identité */}
        {activeSection === 'identity' && (
          <>
            {/* Photo de profil */}
            <Card className="border-2 border-slate-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                  Photo de profil
                </CardTitle>
                <CardDescription>
                  Votre photo sera affichée sur votre espace membre
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <ImageUploader
                  currentImageUrl={formData.photo_profil_url}
                  onImageUploaded={handleImageUploaded}
                  type="avatar"
                  getAuthHeader={getAuthHeader}
                />
              </CardContent>
            </Card>

            {/* Informations personnelles */}
            <Card className="border-2 border-slate-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <FileText className="h-5 w-5 text-emerald-600" />
                  </div>
                  Informations personnelles
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                      <User className="h-4 w-4 text-slate-500" />
                      Prénom *
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base font-medium"
                      required
                      placeholder="Votre prénom"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                      <User className="h-4 w-4 text-slate-500" />
                      Nom *
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base font-medium"
                      required
                      placeholder="Votre nom"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                      <Calendar className="h-4 w-4 text-slate-500" />
                      Date de naissance
                    </label>
                    <DatePickerFr
                      value={formData.date_naissance}
                      onChange={(v) => handleChange({ target: { name: 'date_naissance', value: v } })}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                      <Users className="h-4 w-4 text-slate-500" />
                      Genre
                    </label>
                    <select
                      name="genre"
                      value={formData.genre}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                    >
                      <option value="">-- Sélectionner --</option>
                      <option value="homme">Homme</option>
                      <option value="femme">Femme</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Section: Coordonnées */}
        {activeSection === 'contact' && (
          <Card className="border-2 border-slate-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Phone className="h-5 w-5 text-purple-600" />
                </div>
                Coordonnées
              </CardTitle>
              <CardDescription>
                Vos informations de contact
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <EmailInput
                    value={formData.email}
                    onChange={(val) => setFormData(prev => ({ ...prev, email: val }))}
                    placeholder="votre@email.fr"
                    required
                    label="Email"
                  />
                </div>

                <div>
                  <PhoneInput
                    value={formData.telephone}
                    onChange={(val) => setFormData(prev => ({ ...prev, telephone: val }))}
                    placeholder="Numéro de téléphone"
                    required
                    label="Téléphone"
                    showLabel={true}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <MapPin className="h-4 w-4 text-slate-500" />
                    Adresse complète
                  </label>
                  <input
                    type="text"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    placeholder="Numéro et nom de rue"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Hash className="h-4 w-4 text-slate-500" />
                    Code postal
                  </label>
                  <input
                    type="text"
                    name="code_postal"
                    value={formData.code_postal}
                    onChange={handleChange}
                    placeholder="Ex: 75001"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Building2 className="h-4 w-4 text-slate-500" />
                    Ville
                  </label>
                  <input
                    type="text"
                    name="ville"
                    value={formData.ville}
                    onChange={handleChange}
                    placeholder="Ex: Paris"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div className="md:col-span-2">
                  <CountrySelect
                    value={formData.pays}
                    onChange={(val) => setFormData(prev => ({ ...prev, pays: val }))}
                    label="Pays"
                    showLabel={true}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section: Professionnel */}
        {activeSection === 'professional' && (
          <Card className="border-2 border-slate-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Briefcase className="h-5 w-5 text-amber-600" />
                </div>
                Informations professionnelles
              </CardTitle>
              <CardDescription>
                Vos informations liées à votre activité professionnelle
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Briefcase className="h-4 w-4 text-slate-500" />
                    Profession
                  </label>
                  <input
                    type="text"
                    name="profession"
                    value={formData.profession}
                    onChange={handleChange}
                    placeholder="Ex: Maçon, Électricien..."
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Star className="h-4 w-4 text-slate-500" />
                    Statut d'emploi
                  </label>
                  <select
                    name="statut_emploi"
                    value={formData.statut_emploi}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                  >
                    <option value="">-- Sélectionner --</option>
                    <option value="salarie">Salarié</option>
                    <option value="independant">Indépendant</option>
                    <option value="chef_entreprise">Chef d'entreprise</option>
                    <option value="retraite">Retraité</option>
                    <option value="demandeur_emploi">Demandeur d'emploi</option>
                    <option value="etudiant">Étudiant</option>
                  </select>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Home className="h-4 w-4 text-slate-500" />
                    Type de logement
                  </label>
                  <select
                    name="type_logement"
                    value={formData.type_logement}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                  >
                    <option value="">-- Sélectionner --</option>
                    <option value="proprietaire">Propriétaire</option>
                    <option value="locataire">Locataire</option>
                    <option value="heberge">Hébergé</option>
                  </select>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Users className="h-4 w-4 text-slate-500" />
                    Personnes au foyer
                  </label>
                  <input
                    type="number"
                    name="nombre_foyer"
                    value={formData.nombre_foyer}
                    onChange={handleChange}
                    min="1"
                    max="20"
                    placeholder="Nombre de personnes"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Star className="h-4 w-4 text-slate-500" />
                    Activité professionnelle (BTP)
                  </label>
                  <UnifiedActivitySelector
                    value={formData.activite_professionnelle}
                    onChange={(activity) => setFormData(prev => ({ 
                      ...prev, 
                      activite_professionnelle: activity 
                    }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section: Sécurité */}
        {activeSection === 'security' && (
          <ChangePasswordForm 
            getAuthHeader={getAuthHeader}
            onSuccess={() => toast.success('Mot de passe modifié avec succès !')}
          />
        )}

        {/* Bouton de sauvegarde (sauf pour sécurité) */}
        {activeSection !== 'security' && (
          <div className="flex justify-end pt-4">
            <Button
              type="submit"
              disabled={saving}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all text-base font-semibold"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Enregistrer les modifications
                </>
              )}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
